import
  React, {
  useContext,
  useState
} from 'react';

import {
  useHistory
} from 'react-router-dom';

import {
  Carousel,
  Image,
  JourneyProvider,
  ServiceCard,
  Text,
  VContainer,
  VContainerVH
} from '@vodacom/web-common';

import {
  Environment
} from '@vodacom/web-env';

import {
  CommonProvider,
  DrupalProvider
} from 'providers';

import {
  AdSlot,
  Hero,
  ModalCard,
  RoundInfoButton
} from 'components';

import {
  AdNames,
  CacheKeys,
  CustomColors,
  FeatureToggles,
  Icons,
  OriginContexts,
  UserTypes
} from '../../../constants';

import {
  NewsFeed,
  Offers,
  SearchBar,
  SocialBundleWidget,
  WeatherWidget
} from 'journeys';

import {
  HandleRender,
  withScreenViewTagging
} from 'hocs';

import {
  getStorageWithExpiry,
  handleUtagLinkEvent,
  isEqual,
  isFeatureEnabled,
  openDeeplink,
  setStorageWithExpiry,
  useRedirectHandler
} from 'helpers';

import {
  AppContext
} from 'context';

const HomeUI = () => {

  const history = useHistory();
  const { config } = useContext(AppContext);

  const context = JourneyProvider.get(CacheKeys.ConfigContext, 'value', '');
  const redirectHandler = useRedirectHandler();

  HomeUI.tagViewData = {
    ...HomeUI.tagViewData,
    visitor_customer_segment: getStorageWithExpiry('msisdn', 'msisdnType', ''),
    visitor_id_asset_active: getStorageWithExpiry('msisdn', 'hashedMsisdn', ''),
    enc_customer_id: getStorageWithExpiry('msisdn', 'encMsisdn', '') ? true : false,
    customer_city: getStorageWithExpiry('Location', 'city', 'Johannesburg')
  };

  const [ state, setState ] = React.useState({
    showHomeNotification: getStorageWithExpiry('Common', 'showHomeNotification', 'yes')
  });

  const [ renderAd, setRenderAd ] = useState(false);

  const [ pillarMetaState, setPillarMetaState ] = React.useState({
    pillarMeta: [],
    hasError: false,
    loading: true,
    error: null
  });

  const [ location, setLocation ] = useState(null);

  const [ promotionBannerMetaState, setPromotionBannerMetaState ] = React.useState({
    promoBannerMeta: [],
    hasError: false,
    loading: true,
    error: null
  });

  const [ promotionsState, setPromotionsState ] = React.useState({
    promotions: [],
    hasError: false,
    loading: true,
    error: null
  });

  const getMsisdnDetails = async () => {

    if (getStorageWithExpiry('msisdn', 'hashedMsisdn') && getStorageWithExpiry('msisdn', 'msisdnType')) {
      return;
    }

    const res = await CommonProvider.getMsisdnDetails(getStorageWithExpiry('msisdn', 'encMsisdn'));

    if (!res?.ok || !res?.result) {
      return;
    }

    res?.result?.id && setStorageWithExpiry('msisdn', 'hashedMsisdn', res?.result?.id, false, 30 * 24 * 3600 * 1000);
    res?.result?.type && setStorageWithExpiry('msisdn', 'msisdnType', res?.result?.type, false, 30 * 24 * 3600 * 1000);
  };

  const getMenu = async () => {

    return await DrupalProvider.getCachedItem(
      CacheKeys.Menu,
      null,
      DrupalProvider.getMenu
    );
  };

  const getTemplates = async () => {

    return await DrupalProvider.getCachedItem(
      CacheKeys.PageTemplates,
      null,
      DrupalProvider.getPageTemplates
    );
  };

  const getPillarsMeta = async () => {

    const response = await DrupalProvider.getCachedItem(
      CacheKeys.PillarMeta,
      null,
      DrupalProvider.getPillarMeta
    );

    if (!response?.ok) {
      setPillarMetaState({
        ...pillarMetaState,
        hasError: true,
        loading: false,
        error: response?.error
      });
      return;
    }

    setPillarMetaState({
      ...pillarMetaState,
      pillarMeta: response?.result,
      loading: false
    });
  };

  const getServiceCardsCarousel = () => {
    const serviceCards = [];

    for (const service of pillarMetaState.pillarMeta) {
      if (!service) {
        continue;
      }

      const goToListPage = () => {

        const category = service.category.toLowerCase();

        handleUtagLinkEvent({
          slide_title: `cbu: connectu: home page: free services: ${category}`,
          event_action: 'click',
          event_category: 'slider',
          event_label: 'continue'
        });

        return history.push(`/pillars/${category}`);
      };

      const icon = service.category.replace(/[^0-9a-z-A-Z]/g, '');

      serviceCards.push(
        <ServiceCard
          key={service.category.toLowerCase()}
          heading={service.title}
          body={service.description}
          backgroundImageUrl={`${Environment.current.drupalBaseUrl}${service.cardImage}`}
          iconUrl={Icons[icon]}
          onClick={goToListPage}
        />
      );
    }

    return serviceCards;
  };

  const geolocationError = (error) => {

    switch (error.code) {
      case error.PERMISSION_DENIED:
        console.log('User denied the request for Geolocation.');
        break;

      case error.POSITION_UNAVAILABLE:
        console.log('Location information is unavailable.');
        break;

      case error.TIMEOUT:
        console.log('The request to get user location timed out.');
        break;

      default:
        console.log('An unknown error occurred.');
        break;
    }
  };

  const useLocation = async (position) => {

    setStorageWithExpiry('Location', 'city', null);
    setStorageWithExpiry('Location', 'latitude', position?.coords?.latitude);
    setStorageWithExpiry('Location', 'longitude', position?.coords?.longitude);

    setLocation(position?.coords);
  };

  const getLocation = async () => {

    if (navigator.geolocation) {

      navigator.geolocation.getCurrentPosition(
        useLocation,
        geolocationError,
        {
          timeout: Environment.current.locationTimeout
        }
      );

    }
  };

  const getPromotionBanner = async () => {

    if (!isFeatureEnabled(config, FeatureToggles.EnablePromotionBanner)) {
      return;
    }

    const response = await DrupalProvider.getCachedItem(
      CacheKeys.PromotionBanner,
      null,
      DrupalProvider.getPromotionBanner
    );

    if (!response || !response.ok) {
      setPromotionBannerMetaState({
        ...promotionBannerMetaState,
        hasError: true,
        loading: false,
        error: true
      });
      return;
    }

    setPromotionBannerMetaState({
      ...promotionBannerMetaState,
      promoBannerMeta: response?.result?.[0]?.content,
      loading: false
    });

  };

  const buildPromotionBanner = () => {

    if (
      !promotionBannerMetaState.promoBannerMeta
      || !Array.isArray(promotionBannerMetaState.promoBannerMeta)
      || !promotionBannerMetaState?.promoBannerMeta?.length > 0
    ) {
      return;
    }

    let promotionBanner = promotionBannerMetaState.promoBannerMeta[0];

    if (!promotionBanner.title || !promotionBanner.url) {
      return;
    }

    return (

      <Hero
        background={`${Environment.current.drupalBaseUrl}${promotionBanner.image}`}
        bannerHeight={'190px'}
        bannerPadding={'16px 24px'}
        useOverLay={true}
        subtitle={promotionBanner.caption}
        subtitlePadding={'8px 0 8px 0'}
        subtitleFontSize={'12px'}
        title={promotionBanner.title}
        clickOutUrl={promotionBanner.url.uri}
        clickOutText={promotionBanner.url.title}
        onClick={() => {

          handleUtagLinkEvent({
            event_name: 'campaign_internal_click',
            link_id: `cbu: connectu: home: discover: ${promotionBanner.title.toLowerCase()}`
          });

          if (promotionBanner.vodaypayAppId && isEqual(context, OriginContexts.VodaPay)) {

            openDeeplink(promotionBanner.vodaypayAppId);

            return;
          }

          redirectHandler(
            history.location,
            `/redirect?targetUrl=${encodeURIComponent(promotionBanner.url.uri)}&isZeroRated=false`,
            false
          );
          return;
        }} />
    );
  };

  const getPromotions = async () => {

    if (!isFeatureEnabled(config, FeatureToggles.EnablePromotions)) {
      return;
    }

    const response = await DrupalProvider.getCachedItem(
      CacheKeys.Promotions,
      null,
      DrupalProvider.getPromotions
    );

    if (!response || !response?.ok) {
      setPromotionsState({
        ...promotionsState,
        hasError: true,
        loading: false,
        error: response?.error
      });
      return;
    }

    setPromotionsState({
      ...promotionsState,
      promotions: response?.result?.[0]?.content,
      loading: false
    });
  };

  const getOnPromotionCardsCarousel = () => {
    const promotionCards = [];

    for (const promotion of promotionsState.promotions) {
      if (!promotion || !promotion.title || !promotion.url) {
        continue;
      }

      const goToPromotionLink = () => {

        handleUtagLinkEvent({
          event_name: 'campaign_internal_click',
          link_id: `cbu: connectu: home: discover carousel: ${promotion.title.toLowerCase()}: position: ${promotionsState.promotions.indexOf(promotion)}`
        });

        return redirectHandler(
          history.location,
          `/redirect?targetUrl=${encodeURIComponent(promotion.url)}&isZeroRated=false`,
          false
        );
      };

      promotionCards.push(
        <Image
          key={promotion.title.toLowerCase()}
          height={'110%'}
          width={'110%'}
          src={`${Environment.current.drupalBaseUrl}${promotion.image}`}
          onClick={goToPromotionLink} />
      );
    }

    return promotionCards;
  };

  React.useEffect(() => {

    const init = async () => {
      try {
        await getMenu();
        await getPromotionBanner();
        await getPillarsMeta();
        await getPromotions();
        await getTemplates();
        await getMsisdnDetails();
      } catch {
        // TODO: Figure out what to do with an error
      } finally {
        setRenderAd(true);
      }
    };

    init();

    // TODO: return cleanup function for async calls on unmount

  }, []);

  return (
    <VContainer padding={'0 0 50px 0'}>

      { config?.enableHomeNotification && state.showHomeNotification === 'yes' &&

          <ModalCard
            isSuccess={false}
            padding={'20px'}
            customContent={config?.homeNotification?.value}
            pageName={'cbu: connectu: home notification'}
            onClose={() => {

              setStorageWithExpiry('Common', 'showHomeNotification', 'no');
              setState(prevState => ({
                ...prevState,
                showHomeNotification: 'no'
              }));
            }}/>
      }

      <Offers
        onAgree={getLocation}/>

      {buildPromotionBanner()}

      { getStorageWithExpiry('msisdn', 'msisdnType', '') === UserTypes.Prepaid &&

        <SocialBundleWidget/>
      }

      <VContainer
        margin={'16px 0 0'}>

        <Text
          style={{
            padding: '16px',
            fontWeight: 'bold'
          }}>
          {'Free services'}
        </Text>

        <HandleRender
          isLoading={pillarMetaState.loading || pillarMetaState.hasError}
          loader={{ size: '50', margin: '16px' }} >

          {
            pillarMetaState.pillarMeta && pillarMetaState.pillarMeta.length > 0
            && <Carousel
              componentList={getServiceCardsCarousel()}
              autoSwipeTime={4000} />
          }
        </HandleRender>
      </VContainer>

      { isFeatureEnabled(config, FeatureToggles.EnablePromotions) &&
        <VContainer
          margin={'0 0 16px'}>

          <Text
            style={{
              padding: '16px',
              fontWeight: 'bold'
            }}>
            {'On Promotion'}
          </Text>

          <HandleRender
            isLoading={promotionsState.loading || promotionsState.hasError}
            loader={{ size: '50', margin: '16px' }} >

            {
              <VContainer
                margin={'0 16px'}>

                <Carousel
                  componentList={getOnPromotionCardsCarousel()}
                  slidesPerView={2.4}
                  autoSwipeTime={4000} />
              </VContainer>
            }
          </HandleRender>
        </VContainer>
      }

      { isFeatureEnabled(config, FeatureToggles.EnableSearch) &&

        <VContainerVH
          padding='9px 13px'
          margin='17px 0 32px'
          boxShadow={`0px 5px 15px 0px ${CustomColors.TransparentBlack1}`}
          borderRadius={'4px'}>
          <SearchBar
            onSearchClick={(searchTerm) => {

              handleUtagLinkEvent({
                search:'search_apply',
                search_term: searchTerm,
                page_tool_name: 'search component',
                page_tool_event: 'use',
                page_section: 'search'
              });

              history.push(`/search?term=${searchTerm}`);
            }}
          />
        </VContainerVH>
      }

    {
      renderAd &&
      <AdSlot
        margin={'0 0 32px 0'}
        adId={AdNames.MiddleLeaderboard}/>
    }

      <NewsFeed
        margin={'0 0 0 6px'}
        numberOfTrends={5}
        showTrendsButton={true}/>

      {
        renderAd &&
        <AdSlot
          margin={'12px 0 32px'}
          adId={AdNames.BottomLeaderboard}/>
      }

      <WeatherWidget
        {...location}
        onClick={() => history.push('/weather')}
      />

      <RoundInfoButton />
    </VContainer>
  );
};

HomeUI.pageName = 'cbu: connectu: home';

HomeUI.tagViewData = {
  journey_event: 'journey_start,journey_end',
  journey_name: 'home',
  journey_type: 'home',
  visitor_type: 'consumer',
  page_channel: 'home',
  site_section: 'home'
};

export const Home = withScreenViewTagging(HomeUI);
